/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  Legend, AreaChart, Area,
} from "recharts";
import {
  CurrencyInr, ChartLine, Users, Storefront, Download, ArrowUp, ArrowDown,
  Calendar, Receipt, ShoppingBag, Truck, Stethoscope, TestTube,
  Lightning, Percent, ArrowRight,
} from "@phosphor-icons/react";

const SRC_META = {
  waste_invoice:        { color: "#2D5A27", Icon: Receipt,      lbl: "Waste" },
  commerce_sale:        { color: "#0B2B66", Icon: ShoppingBag,  lbl: "Retail" },
  commerce_rental:      { color: "#5C5A55", Icon: Truck,        lbl: "Rentals" },
  health_doctor:        { color: "#6E8A4A", Icon: Stethoscope,  lbl: "Doctor" },
  health_lab:           { color: "#D4A418", Icon: TestTube,     lbl: "Lab" },
  health_ambulance:     { color: "#7A2E18", Icon: Lightning,    lbl: "Ambulance" },
  platform_commission:  { color: "#C9A227", Icon: Percent,      lbl: "Commission" },
};

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function FinanceRollup() {
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(today());
  const [summary, setSummary] = useState(null);
  const [clv, setClv] = useState(null);
  const [partners, setPartners] = useState(null);
  const [ts, setTs] = useState(null);
  const [gran, setGran] = useState("day");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { from, to };
      const [s, c, p, t] = await Promise.all([
        api.get("/admin/finance/rollup/summary", { params }),
        api.get("/admin/finance/rollup/customer-clv", { params: { ...params, limit: 50 } }),
        api.get("/admin/finance/rollup/partner-performance", { params: { ...params, limit: 50 } }),
        api.get("/admin/finance/rollup/timeseries", { params: { ...params, granularity: gran } }),
      ]);
      setSummary(s.data); setClv(c.data); setPartners(p.data); setTs(t.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load rollup"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [from, to, gran]);

  const PRESETS = [
    ["7d", 6],
    ["30d", 29],
    ["90d", 89],
    ["This year", 365],
  ];
  const setPreset = (n) => { setFrom(daysAgo(n)); setTo(today()); };

  const downloadCsv = async (path, name) => {
    try {
      const r = await api.get(path, { params: { from, to }, responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("CSV download failed"); }
  };

  return (
    <AppShell title="Finance Rollup" subtitle="Module 12 · Unified billing & CRM">
      {/* Filter bar */}
      <div className="bg-white border border-[#E5E2D9] rounded-md p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="rollup-filters">
        <div className="flex items-center gap-2">
          <Calendar size={13} color="#5C5A55"/>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                  data-testid="rollup-from"
                  className="h-8 border border-[#E5E2D9] rounded px-2 text-xs"/>
          <span className="text-[#8A8780] text-xs">→</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
                  data-testid="rollup-to"
                  className="h-8 border border-[#E5E2D9] rounded px-2 text-xs"/>
        </div>
        <div className="flex gap-1">
          {PRESETS.map(([lbl, n]) => (
            <button key={lbl} onClick={() => setPreset(n)}
                     className="h-8 px-2.5 rounded text-[11px] border border-[#E5E2D9] hover:border-[#2D5A27] text-[#5C5A55]">
              {lbl}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-1.5">
          <button onClick={() => downloadCsv("/admin/finance/rollup/export/revenue.csv", `revenue-${from}-${to}.csv`)}
                   data-testid="export-revenue-csv"
                   className="h-8 px-3 rounded text-xs border border-[#E5E2D9] hover:border-[#2D5A27] text-[#5C5A55] inline-flex items-center gap-1.5">
            <Download size={11}/> Revenue CSV
          </button>
          <button onClick={() => downloadCsv("/admin/finance/rollup/export/customer-clv.csv", `customer-clv-${from}-${to}.csv`)}
                   data-testid="export-clv-csv"
                   className="h-8 px-3 rounded text-xs border border-[#E5E2D9] hover:border-[#2D5A27] text-[#5C5A55] inline-flex items-center gap-1.5">
            <Download size={11}/> CLV CSV
          </button>
          <button onClick={() => downloadCsv("/admin/finance/rollup/export/partner-performance.csv", `partner-perf-${from}-${to}.csv`)}
                   data-testid="export-partner-csv"
                   className="h-8 px-3 rounded text-xs border border-[#E5E2D9] hover:border-[#2D5A27] text-[#5C5A55] inline-flex items-center gap-1.5">
            <Download size={11}/> Partner CSV
          </button>
        </div>
      </div>

      {loading && !summary && <div className="text-sm text-[#8A8780]">Loading…</div>}

      {summary && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="rollup-kpis">
            <Kpi label="Booked revenue" value={fmt(summary.totals.booked)}
                  pct={summary.totals.booked_pct_change}
                  prev={fmt(summary.totals.booked_prev)} accent/>
            <Kpi label="Collected" value={fmt(summary.totals.collected)}
                  pct={summary.totals.collected_pct_change}
                  prev={fmt(summary.totals.collected_prev)}/>
            <Kpi label="Outstanding" value={fmt(summary.totals.outstanding)} danger/>
            <Kpi label={`${summary.customers.unique} customers · ${summary.customers.repeat_rate_pct}% repeat`}
                  value={summary.customers.repeat + " repeats"}/>
          </div>

          {/* Source breakdown */}
          <div className="bg-white border border-[#E5E2D9] rounded-md p-6 mb-6" data-testid="source-breakdown">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Revenue by source</div>
                <div className="font-heading text-lg">Where the money comes from</div>
              </div>
              <div className="text-xs text-[#8A8780]">Booked vs Collected · period {summary.window.from} → {summary.window.to}</div>
            </div>
            <div className="space-y-3">
              {summary.by_source.map(s => {
                const m = SRC_META[s.source] || {};
                const maxBooked = Math.max(...summary.by_source.map(x => x.booked || 1), 1);
                const wB = Math.max(2, (s.booked / maxBooked) * 100);
                const wC = (s.collected / maxBooked) * 100;
                return (
                  <div key={s.source} className="flex items-center gap-4">
                    <div className="w-32 flex items-center gap-2 text-xs">
                      {m.Icon && <m.Icon size={12} color={m.color} weight="duotone"/>}
                      <span className="font-medium text-[#1A1A1A]">{s.label}</span>
                    </div>
                    <div className="flex-1 relative h-7 bg-[#F7F6F2] rounded overflow-hidden">
                      <div className="absolute inset-y-0 left-0 opacity-30" style={{ width: `${wB}%`, background: m.color }}/>
                      <div className="absolute inset-y-0 left-0" style={{ width: `${wC}%`, background: m.color }}/>
                      <div className="absolute inset-0 flex items-center px-2 text-[11px] font-mono justify-between">
                        <span className="text-white drop-shadow">{s.count} ·</span>
                        <span className="text-[#1A1A1A]">{fmt(s.booked)} <span className="text-[#5C5A55]">({fmt(s.collected)} collected)</span></span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Timeseries */}
          {ts && (
            <div className="bg-white border border-[#E5E2D9] rounded-md p-6 mb-6" data-testid="timeseries-chart">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Trend</div>
                  <div className="font-heading text-lg">Booked revenue across time</div>
                </div>
                <div className="flex gap-1">
                  {[["day", "Daily"], ["month", "Monthly"]].map(([k, l]) => (
                    <button key={k} onClick={() => setGran(k)}
                             className={`h-8 px-3 rounded text-[11px] border ${gran === k ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "border-[#E5E2D9] text-[#5C5A55]"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={ts.series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9"/>
                    <XAxis dataKey="date" fontSize={10} stroke="#5C5A55"/>
                    <YAxis fontSize={10} stroke="#5C5A55"/>
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E2D9", fontSize: 12 }}
                              formatter={(v) => fmt(v)}/>
                    <Legend wrapperStyle={{ fontSize: 10 }}/>
                    {ts.sources.map(src => (
                      <Area key={src.key} type="monotone" dataKey={src.key} name={SRC_META[src.key]?.lbl || src.label}
                             stackId="1" stroke={SRC_META[src.key]?.color}
                             fill={SRC_META[src.key]?.color} fillOpacity={0.5}/>
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              {ts.series.length === 0 && <div className="text-xs text-[#8A8780] text-center mt-3">No data in window.</div>}
            </div>
          )}

          {/* Tables: CLV + Partner perf */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
            <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden" data-testid="clv-table">
              <div className="px-6 py-4 border-b border-[#F0ECE3] flex items-center gap-2">
                <Users size={14} color="#2D5A27"/>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Top customers</div>
                  <div className="font-heading">CLV in window · top 50</div>
                </div>
                <div className="ml-auto text-xs text-[#5C5A55]">{clv?.total ?? 0} total</div>
              </div>
              <div className="max-h-[400px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#F7F6F2] sticky top-0 text-[10px] uppercase tracking-[0.15em] text-[#5C5A55]">
                    <tr>
                      <th className="p-3 text-left">Customer</th>
                      <th className="p-3 text-right">Orders</th>
                      <th className="p-3 text-right">Booked</th>
                      <th className="p-3 text-right">Collected</th>
                      <th className="p-3">Sources</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(clv?.customers || []).map(c => (
                      <tr key={c.customer_id} className="border-t border-[#F0ECE3]">
                        <td className="p-3">
                          <div className="font-medium text-[#1A1A1A]">{c.name || "—"}</div>
                          <div className="text-[#8A8780] text-[10px]">{c.phone}</div>
                        </td>
                        <td className="p-3 text-right">{c.orders}</td>
                        <td className="p-3 text-right font-mono text-[#2D5A27]">{fmt(c.booked)}</td>
                        <td className="p-3 text-right font-mono">{fmt(c.collected)}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {(c.sources || []).map(s => (
                              <span key={s} className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-[0.1em]"
                                     style={{ color: SRC_META[s]?.color, background: `${SRC_META[s]?.color}1A` }}>
                                {SRC_META[s]?.lbl || s}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!clv?.customers?.length && (
                      <tr><td colSpan={5} className="p-8 text-center text-[#8A8780]">No customer activity</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden" data-testid="partner-table">
              <div className="px-6 py-4 border-b border-[#F0ECE3] flex items-center gap-2">
                <Storefront size={14} color="#0B2B66"/>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">Top partners</div>
                  <div className="font-heading">Partner performance</div>
                </div>
                <div className="ml-auto text-xs text-[#5C5A55]">{partners?.total ?? 0} total</div>
              </div>
              <div className="max-h-[400px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#F7F6F2] sticky top-0 text-[10px] uppercase tracking-[0.15em] text-[#5C5A55]">
                    <tr>
                      <th className="p-3 text-left">Partner</th>
                      <th className="p-3 text-right">Orders</th>
                      <th className="p-3 text-right">AOV</th>
                      <th className="p-3 text-right">Revenue</th>
                      <th className="p-3 text-right">Commission</th>
                      <th className="p-3 text-right">Compl.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(partners?.partners || []).map(p => (
                      <tr key={p.partner_id} className="border-t border-[#F0ECE3]">
                        <td className="p-3">
                          <div className="font-medium text-[#1A1A1A]">{p.name}</div>
                          <div className="text-[#8A8780] text-[10px] uppercase tracking-[0.15em]">{p.subcategory || p.category}</div>
                        </td>
                        <td className="p-3 text-right">{p.orders}</td>
                        <td className="p-3 text-right font-mono">{fmt(p.aov)}</td>
                        <td className="p-3 text-right font-mono text-[#2D5A27]">{fmt(p.revenue_booked)}</td>
                        <td className="p-3 text-right font-mono text-[#C9A227]">{fmt(p.commission_booked)}</td>
                        <td className="p-3 text-right">{p.completion_rate_pct}%</td>
                      </tr>
                    ))}
                    {!partners?.partners?.length && (
                      <tr><td colSpan={6} className="p-8 text-center text-[#8A8780]">No partner activity</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
}

function Kpi({ label, value, pct, prev, accent, danger }) {
  const arrow = pct == null ? null : pct >= 0 ? ArrowUp : ArrowDown;
  const Arr = arrow;
  return (
    <div className={`bg-white border border-[#E5E2D9] rounded-md p-4 ${danger ? "border-l-4 border-l-[#C25D38]" : accent ? "border-l-4 border-l-[#2D5A27]" : ""}`}>
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">{label}</div>
      <div className="font-heading text-2xl mt-2">{value}</div>
      {pct != null && (
        <div className={`text-[11px] mt-1 inline-flex items-center gap-1 ${pct >= 0 ? "text-[#2D5A27]" : "text-[#C25D38]"}`}>
          <Arr size={10} weight="bold"/> {Math.abs(pct)}% vs {prev}
        </div>
      )}
    </div>
  );
}
